import clsx from 'clsx';
import { ArrowRight, Pause, Play, RotateCcw } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../../lib/api';
import type { LessonPayload } from '../../../lib/types';
import { Button, Card } from '../../../ui';
import { QuestionCard } from '../QuestionCard';

interface Props {
  lesson: LessonPayload;
  onFinished: () => void;
  advancing: boolean;
}

const fmt = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Plays the lesson video and pauses at each "Pause & Try" checkpoint.
 * A checkpoint must be finished before the video continues past it.
 * While final videos are in production, a placeholder timeline stands in.
 */
export function VideoStage({ lesson, onFinished, advancing }: Props) {
  const { video, checkpoints } = lesson;
  const [done, setDone] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(checkpoints.map((c) => [c.id, c.state.done])),
  );
  const firstPending = checkpoints.find((c) => !c.state.done);
  const [position, setPosition] = useState(() =>
    Math.min(lesson.progress.videoPositionMs, firstPending ? firstPending.atMs : video.durationMs),
  );
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [captionsOn, setCaptionsOn] = useState(false);
  useEffect(() => {
    const track = videoRef.current?.textTracks[0];
    if (track) track.mode = captionsOn ? 'showing' : 'hidden';
  }, [captionsOn]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSaved = useRef(position);

  const nextCheckpoint = useMemo(
    () => checkpoints.find((c) => !done[c.id] && c.atMs <= position + 250),
    [checkpoints, done, position],
  );
  // Streams often stop a moment before the last frame (audio a little longer than
  // video), so the final 2.5 s count as finished.
  const END_TOLERANCE_MS = 2_500;
  const ended = position >= video.durationMs - (video.placeholder ? 250 : END_TOLERANCE_MS);
  const allDone = checkpoints.every((c) => done[c.id]);

  // Pause at a checkpoint.
  useEffect(() => {
    if (nextCheckpoint && !active) {
      setPlaying(false);
      videoRef.current?.pause();
      setActive(nextCheckpoint.id);
    }
  }, [nextCheckpoint, active]);

  // Adaptive streaming (HLS). Safari plays HLS natively; other browsers use hls.js.
  useEffect(() => {
    const el = videoRef.current;
    if (video.placeholder || !el || !video.src.endsWith('.m3u8')) return;
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = video.src;
      return;
    }
    let destroyed = false;
    let hls: { destroy(): void } | null = null;
    void import('hls.js').then(({ default: Hls }) => {
      if (destroyed || !Hls.isSupported()) return;
      const h = new Hls({ capLevelToPlayerSize: true, maxBufferLength: 30 });
      h.loadSource(video.src);
      h.attachMedia(el);
      hls = h;
    });
    return () => {
      destroyed = true;
      hls?.destroy();
    };
  }, [video.placeholder, video.src]);

  const doneRef = useRef(done);
  doneRef.current = done;
  /** Never play past an unanswered checkpoint: clamp to it. */
  const clampToCheckpoint = useCallback(
    (ms: number) => {
      const stop = checkpoints.find((c) => !doneRef.current[c.id] && c.atMs <= ms);
      return stop ? stop.atMs : ms;
    },
    [checkpoints],
  );

  /** Reads the real video's time, stopping at the next unanswered checkpoint. */
  const syncFromVideo = useCallback(
    (el: HTMLVideoElement) => {
      const raw = el.currentTime * 1000;
      const clamped = clampToCheckpoint(raw);
      if (clamped < raw) {
        el.pause();
        el.currentTime = clamped / 1000;
      }
      setPosition(clamped >= video.durationMs - END_TOLERANCE_MS ? video.durationMs : clamped);
    },
    [clampToCheckpoint, video.durationMs],
  );

  // timeupdate stops firing while a stream is stalled, so also poll while playing.
  // If playback stops moving in the last few seconds (stream tail shorter than the
  // declared duration), treat the video as finished instead of leaving the child stuck.
  useEffect(() => {
    if (video.placeholder || !playing) return;
    let lastT = -1;
    let stuckSince = 0;
    const id = setInterval(() => {
      const el = videoRef.current;
      if (!el) return;
      syncFromVideo(el);
      const t = el.currentTime;
      const remainingMs = video.durationMs - t * 1000;
      if (t === lastT && !el.paused && remainingMs < 8_000) {
        stuckSince ||= Date.now();
        if (Date.now() - stuckSince > 2_000) setPosition(video.durationMs);
      } else stuckSince = 0;
      lastT = t;
    }, 500);
    return () => clearInterval(id);
  }, [playing, video.placeholder, video.durationMs, syncFromVideo]);

  // Placeholder clock.
  useEffect(() => {
    if (!video.placeholder || !playing) return;
    const started = performance.now() - position;
    let raf = 0;
    const tick = () => {
      const raw = Math.min(performance.now() - started, video.durationMs);
      const now = clampToCheckpoint(raw);
      setPosition(now);
      if (now < raw || now >= video.durationMs) setPlaying(false);
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, video.placeholder, video.durationMs]);

  // Save the furthest point every ~10 seconds of viewing (advisory only).
  useEffect(() => {
    if (position - lastSaved.current > 10_000) {
      lastSaved.current = position;
      void api.post(`/student/lessons/${lesson.lesson.id}/position`, { positionMs: Math.floor(position) }).catch(() => undefined);
    }
  }, [position, lesson.lesson.id]);

  const toggle = useCallback(() => {
    if (active) return;
    if (ended) {
      setPosition(0);
      if (videoRef.current) videoRef.current.currentTime = 0;
    }
    if (!video.placeholder) {
      const el = videoRef.current;
      if (!el) return;
      if (el.paused) void el.play();
      else el.pause();
      return;
    }
    setPlaying((p) => !p);
  }, [active, ended, video.placeholder]);

  const activeCp = checkpoints.find((c) => c.id === active);

  return (
    <div className="flex flex-col gap-5">
      <div className="relative overflow-hidden rounded-[var(--radius-card)] bg-ink shadow-[var(--shadow-lift)]">
        <div className="relative aspect-video">
          {video.placeholder ? (
            <PlaceholderScene title={lesson.lesson.title} playing={playing} />
          ) : (
            <video
              ref={videoRef}
              className="size-full bg-ink"
              src={video.src.endsWith('.m3u8') ? undefined : video.src}
              crossOrigin="anonymous"
              poster={video.poster}
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={(e) => syncFromVideo(e.currentTarget)}
              onSeeked={(e) => syncFromVideo(e.currentTarget)}
              onLoadedMetadata={(e) => {
                e.currentTarget.currentTime = position / 1000;
                // Lesson videos have captions burned in; the text track is an optional extra.
                const track = e.currentTarget.textTracks[0];
                if (track) track.mode = captionsOn ? 'showing' : 'hidden';
              }}
              onEnded={() => setPosition(video.durationMs)}
            >
              {video.captions && <track kind="captions" src={video.captions} srcLang="en" label="English" default />}
            </video>
          )}

          {createPortal(<AnimatePresence>
            {activeCp?.item && (
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Pause and try"
                className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm sm:p-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  initial={{ y: 30, scale: 0.96 }}
                  animate={{ y: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                  className="max-h-full w-full max-w-2xl overflow-y-auto"
                >
                  <Card className="p-6 sm:p-9">
                    <p className="mb-4 inline-flex rounded-full bg-gold-soft px-3 py-1 text-sm font-bold text-[#8a5a06]">Pause and try</p>
                    <QuestionCard
                      autoRead
                      media={{ voice: activeCp.voice, hintVideo: activeCp.hintVideo, workedVideo: activeCp.workedVideo }}
                      item={activeCp.item}
                      attemptId={lesson.checkpointAttemptId}
                      initial={activeCp.state}
                      continueLabel="Back to the video"
                      onFinished={() => {
                        setDone((d) => ({ ...d, [activeCp.id]: true }));
                        setActive(null);
                        if (video.placeholder) setPlaying(true);
                        else void videoRef.current?.play();
                      }}
                    />
                  </Card>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>, document.body)}

          {!playing && !active && (
            <button
              onClick={toggle}
              aria-label={ended ? 'Watch again' : 'Play'}
              className="absolute inset-0 z-[5] flex items-center justify-center bg-ink/25 transition-colors hover:bg-ink/35"
            >
              <span className="flex size-20 items-center justify-center rounded-full bg-gold text-ink shadow-lg">
                {ended ? <RotateCcw className="size-9" aria-hidden /> : <Play className="size-9 translate-x-0.5 fill-ink" aria-hidden />}
              </span>
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 bg-ink px-4 py-3 text-white">
          <button onClick={toggle} disabled={!!active} aria-label={playing ? 'Pause' : 'Play'} className="flex size-10 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-40">
            {playing ? <Pause className="size-5 fill-white" aria-hidden /> : <Play className="size-5 fill-white" aria-hidden />}
          </button>
          <div className="relative h-2 flex-1 rounded-full bg-white/15" aria-hidden>
            <div className="absolute inset-y-0 left-0 rounded-full bg-gold" style={{ width: `${(position / video.durationMs) * 100}%` }} />
            {checkpoints.map((c) => (
              <span
                key={c.id}
                className={clsx('absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink', done[c.id] ? 'bg-mint' : 'bg-white')}
                style={{ left: `${(c.atMs / video.durationMs) * 100}%` }}
                title="Pause and try"
              />
            ))}
          </div>
          <span className="w-24 text-right font-mono text-sm tabular-nums text-white/70">
            {fmt(position)} / {fmt(video.durationMs)}
          </span>
          {video.captions && !video.placeholder && (
            <button
              onClick={() => setCaptionsOn((c) => !c)}
              aria-pressed={captionsOn}
              aria-label="Captions"
              className={clsx('flex h-8 items-center rounded-md border-2 px-1.5 font-mono text-xs font-bold', captionsOn ? 'border-gold bg-gold text-ink' : 'border-white/50 text-white/80')}
            >
              CC
            </button>
          )}
        </div>
      </div>

      {video.placeholder && (
        <p className="text-center text-sm text-muted">Preview: the final lesson video will play here.</p>
      )}

      <div className="flex justify-end">
        <Button size="lg" disabled={!allDone || !ended} loading={advancing} onClick={onFinished} icon={<ArrowRight className="size-5" aria-hidden />}>
          {allDone && ended ? 'Next: play the game' : 'Watch to the end to continue'}
        </Button>
      </div>
    </div>
  );
}

function PlaceholderScene({ title, playing }: { title: string; playing: boolean }) {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-4 bg-[radial-gradient(ellipse_at_top,#2c2670,#1e1a4d)] p-6 text-center text-white">
      <motion.div
        className="flex gap-3"
        animate={playing ? { y: [0, -6, 0] } : { y: 0 }}
        transition={{ repeat: playing ? Infinity : 0, duration: 1.6, ease: 'easeInOut' }}
        aria-hidden
      >
        {['#5b47e0', '#f2a93b', '#16a57f'].map((c, i) => (
          <motion.span
            key={c}
            className="block size-10 rounded-2xl sm:size-14"
            style={{ background: c }}
            animate={playing ? { rotate: [0, 8, -8, 0] } : { rotate: 0 }}
            transition={{ repeat: playing ? Infinity : 0, duration: 2, delay: i * 0.2 }}
          />
        ))}
      </motion.div>
      <p className="font-display text-2xl font-extrabold sm:text-3xl">{title}</p>
    </div>
  );
}
